import { useEffect, useRef } from "react";

const LOOP_SECONDS = 14;
const WAVE_COUNT = 11;
const PARTICLE_STEP = 13;
const FLOATER_COUNT = 18;

function lerpColor(nx, alpha) {
  const r = Math.round(59 + (168 - 59) * nx);
  const g = Math.round(130 + (85 - 130) * nx);
  const b = Math.round(246 + (247 - 246) * nx);
  return `rgba(${r},${g},${b},${alpha})`;
}

function waveHeight(x, w, h, waveIndex, t, parallax) {
  const baseY = h * (0.48 + (waveIndex / (WAVE_COUNT - 1)) * 0.46);
  const amp = 20 + waveIndex * 4;
  const freq = 0.011 + waveIndex * 0.0012;
  const freq2 = freq * 2.1;
  const travel = t * 2.4;
  const phase = t * 1.15 + waveIndex * 0.65;
  const shiftX = (parallax.x - 0.5) * 1.6;
  const shiftY = (parallax.y - 0.5) * 24;

  return (
    baseY
    + shiftY
    + Math.sin(x * freq - travel + phase + shiftX) * amp
    + Math.sin(x * freq2 + travel * 0.7 - phase * 0.55 + waveIndex * 0.4) * (amp * 0.4)
    + Math.cos(x * 0.005 - travel * 0.35 + phase * 0.3) * (amp * 0.14)
  );
}

function initFloaters(w, h) {
  return Array.from({ length: FLOATER_COUNT }, () => ({
    x: Math.random() * w,
    y: Math.random() * h * 0.42,
    r: 1 + Math.random() * 1.4,
    phase: Math.random() * Math.PI * 2,
    hue: Math.random() > 0.45 ? "blue" : "purple",
    drift: 0.12 + Math.random() * 0.18,
  }));
}

export default function HeroSinusoidalWaves() {
  const canvasRef = useRef(null);
  const floatersRef = useRef([]);
  const rafRef = useRef(0);
  const parallaxRef = useRef({ x: 0.5, y: 0.5, targetX: 0.5, targetY: 0.5 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const speedMultiplier = prefersReduced ? 0.45 : 1;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const dpr = Math.min(window.devicePixelRatio, 2);
      const rect = parent.getBoundingClientRect();
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      floatersRef.current = initFloaters(rect.width, rect.height);
    };

    resize();
    window.addEventListener("resize", resize);

    const onMove = (e) => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();
      parallaxRef.current.targetX = (e.clientX - rect.left) / rect.width;
      parallaxRef.current.targetY = (e.clientY - rect.top) / rect.height;
    };

    const onLeave = () => {
      parallaxRef.current.targetX = 0.5;
      parallaxRef.current.targetY = 0.5;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseleave", onLeave);

    let t = 0;
    let lastFrame = performance.now();

    const draw = (now) => {
      const parent = canvas.parentElement;
      if (!parent) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }

      const w = parent.getBoundingClientRect().width;
      const h = parent.getBoundingClientRect().height;
      if (w === 0 || h === 0) {
        rafRef.current = requestAnimationFrame(draw);
        return;
      }

      const dt = Math.min((now - lastFrame) / 1000, 0.05);
      lastFrame = now;
      t += dt * ((Math.PI * 2) / LOOP_SECONDS) * speedMultiplier;

      ctx.clearRect(0, 0, w, h);

      const parallax = parallaxRef.current;
      parallax.x += (parallax.targetX - parallax.x) * 0.06;
      parallax.y += (parallax.targetY - parallax.y) * 0.06;

      const fadeTop = ctx.createLinearGradient(0, 0, 0, h);
      fadeTop.addColorStop(0, "rgba(2, 6, 23, 0.92)");
      fadeTop.addColorStop(0.32, "rgba(2, 6, 23, 0.55)");
      fadeTop.addColorStop(0.55, "rgba(2, 6, 23, 0.12)");
      fadeTop.addColorStop(1, "rgba(2, 6, 23, 0)");
      ctx.fillStyle = fadeTop;
      ctx.fillRect(0, 0, w, h);

      for (let wave = 0; wave < WAVE_COUNT; wave++) {
        const nz = wave / (WAVE_COUNT - 1);
        const waveFade = 0.28 + nz * 0.62;
        const points = [];

        for (let x = 0; x <= w; x += PARTICLE_STEP) {
          const y = waveHeight(x, w, h, wave, t, parallax);
          const nx = x / w;
          points.push({ x, y, nx });
        }

        ctx.lineWidth = 0.7;
        ctx.lineJoin = "round";
        ctx.lineCap = "round";

        for (let i = 0; i < points.length - 1; i++) {
          const a = points[i];
          const b = points[i + 1];
          const nx = (a.nx + b.nx) * 0.5;
          const pulse = 0.8 + 0.2 * Math.sin(t * 2.2 + nx * Math.PI * 4 + wave);
          ctx.beginPath();
          ctx.strokeStyle = lerpColor(nx, 0.26 * waveFade * pulse);
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }

        for (let i = 0; i < points.length; i++) {
          const pt = points[i];
          const pulse = 0.72 + 0.28 * Math.sin(t * 2.8 + pt.nx * Math.PI * 5 + wave * 0.5);
          const alpha = 0.42 * waveFade * pulse;
          const size = 1.2 + nz * 0.75;

          ctx.beginPath();
          ctx.fillStyle = lerpColor(pt.nx, alpha);
          ctx.arc(pt.x, pt.y, size, 0, Math.PI * 2);
          ctx.fill();

          if (i % 5 === 0) {
            ctx.beginPath();
            ctx.fillStyle = lerpColor(pt.nx, alpha * 0.2);
            ctx.arc(pt.x, pt.y, size * 2.8, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      const floaters = floatersRef.current;
      for (const f of floaters) {
        f.x += (Math.sin(t * 0.7 + f.phase) * f.drift + (parallax.x - 0.5) * 0.4) * speedMultiplier;
        f.y += (Math.cos(t * 0.55 + f.phase) * f.drift * 0.65 + (parallax.y - 0.5) * 0.28) * speedMultiplier;
        if (f.x < 0) f.x = w;
        if (f.x > w) f.x = 0;
        if (f.y < 0) f.y = h * 0.42;
        if (f.y > h * 0.42) f.y = 0;

        const twinkle = 0.35 + 0.65 * Math.sin(t * 2.2 + f.phase);
        const color =
          f.hue === "blue"
            ? `rgba(96, 165, 250, ${0.16 * twinkle})`
            : `rgba(192, 132, 252, ${0.14 * twinkle})`;

        ctx.beginPath();
        ctx.fillStyle = color;
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseleave", onLeave);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div className="hero-sinusoidal-waves" aria-hidden>
      <canvas ref={canvasRef} className="hero-sinusoidal-canvas" />
    </div>
  );
}
